import axiosInstance from '@/services/apiService';

// Define the endpoint for login
const LOGIN_ENDPOINT = '/auth/login';

/**
 * Function to perform login by calling the /api/auth/login endpoint.
 * This function uses the AuthenticationRequestDto to send the login request
 * and returns the AuthenticationResponseDto.
 *
 * @param {Object} credentials - The credentials for login (email, password).
 * @returns {Promise<Object>} - The response containing the JWT token.
 */
const login = async (credentials) => {
    try {
        // Ensure the credentials object matches AuthenticationRequestDto
        const requestBody = {
            email: credentials.email,
            password: credentials.password,
        };

        // Call the login endpoint
        const response = await axiosInstance.post(LOGIN_ENDPOINT, requestBody);

        // Extract the JWT token from AuthenticationResponseDto
        const token = response.data.jwt;

        // Store the token in localStorage
        if (token) {
            localStorage.setItem('token', token);
        }

        return response.data;
    } catch (error) {
        console.error('Login failed:', error);
        throw error;
    }
};

/**
 * Helper function to check if the token is expired
 *
 * @param {String} token - The JWT token to be checked.
 * @returns {Boolean} - True if the token is expired, false otherwise.
 */
const isTokenExpired = (token) => {
    try {
        const decodedToken = JSON.parse(atob(token.split('.')[1]));
        return decodedToken.exp * 1000 < Date.now();
    } catch (e) {
        return true;
    }
};

/**
 * Function to check if the user is authenticated by verifying if a token exists
 * and if it has not expired.
 *
 * @returns {Boolean} - True if the token exists and is valid, false otherwise.
 */
const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    return token && !isTokenExpired(token);
};

/**
 * Function to log out the user by removing the token from localStorage.
 */
const logout = () => {
    localStorage.removeItem('token');
};

export default {
    login,
    isAuthenticated,
    logout,
};